import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Input } from "@jhool-io/fe-components";
import useToast from "../../../../hooks/useToast";
import { IAddRemarkCode, IRemarkCode } from "../../types/remark-codes.types";
import { useEditRemarkCodes } from "../../hooks/remark-code.mutations";

interface EditRemarkCodeProps {
    codeToEdit: IRemarkCode;
    onFormSubmit: () => void;
}

const schema = yup.object().shape({
    code: yup.string().required("Remark code is required"),
    description: yup.string().required("Description is required"),
});

export default function EditRemarkCode({
    codeToEdit,
    onFormSubmit,
}: EditRemarkCodeProps) {
    const editRemarkCode = useEditRemarkCodes(codeToEdit.remark_code_id);
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const {
        register,
        formState: { errors },
        handleSubmit,
    } = useForm<IAddRemarkCode>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const onSubmit = (data: IAddRemarkCode) => {
        editRemarkCode.mutate(data, {
            onSuccess(res) {
                queryClient.invalidateQueries({
                    queryKey: ["remark-codes"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Remark code updated successfully",
                });
                onFormSubmit();
            },
            onError(err) {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update remark code, please try again",
                });
            },
        });
    };

    return (
        <form id="edit-remark-code" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <Input
                    label="Remark Code"
                    {...register("code")}
                    hasError={!!errors.code}
                    errorText={errors.code?.message}
                    defaultValue={codeToEdit.code}
                />
            </div>
            <div className="fg">
                <Input
                    label="Description"
                    {...register("description")}
                    hasError={!!errors.description}
                    errorText={errors.description?.message}
                    defaultValue={codeToEdit.description}
                />
            </div>
        </form>
    );
}
